import { useState } from "react";
import { Filter, X } from "lucide-react";

const field = "h-9 rounded-md border border-[#22252F] bg-[#0F1116] px-2 text-xs text-white";

export const EMPTY_FILTERS = {
  client_id: "", crm_status: "", country: "", agent_id: "", created_from: "", created_to: "",
  last_login_from: "", last_login_to: "", deposits: "", min_deposited: "",
  min_balance: "", max_balance: "",
};

export const cleanFilters = (filters) =>
  Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v !== null));

/** Barra de filtros comerciais reutilizada pelo painel Admin e pelo Gestor/Agente. */
export const ClientFilters = ({ meta, agents = [], filters, onChange, onReset }) => {
  const [open, setOpen] = useState(false);
  const active = Object.values(filters).filter((v) => v !== "").length;
  const set = (key) => (e) => onChange({ ...filters, [key]: e.target.value });
  const allStatuses = [...(meta?.statuses || []), ...(meta?.derived || [])];

  return (
    <div data-testid="client-filters" className="rounded-md border border-[#22252F] bg-[#12141A]">
      <button data-testid="toggle-filters" onClick={() => setOpen(!open)}
              className="flex w-full items-center gap-2 px-4 py-3 text-sm text-[#94A3B8] transition-colors hover:text-white">
        <Filter className="h-4 w-4" /> Filtros
        {active > 0 ? (
          <span data-testid="active-filters-count"
                className="rounded-full bg-[#2563EB] px-2 py-0.5 text-[10px] text-white">{active}</span>
        ) : null}
        <span className="ml-auto text-xs">{open ? "ocultar" : "mostrar"}</span>
      </button>

      {open ? (
        <div className="grid gap-3 border-t border-[#22252F] p-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">ID do cliente</span>
            <input data-testid="filter-client-id" value={filters.client_id} onChange={set("client_id")}
                   placeholder="Ex.: 6a88f48ad2ea636eb9c0b507" className={`${field} w-full font-mono`} />
          </label>

          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">Status comercial</span>
            <select data-testid="filter-crm-status" value={filters.crm_status} onChange={set("crm_status")}
                    className={`${field} w-full`}>
              <option value="">Todos os status</option>
              {allStatuses.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">País</span>
            <input data-testid="filter-country" value={filters.country} onChange={set("country")}
                   placeholder="PT, BR, ES…" className={`${field} w-full uppercase`} />
          </label>

          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">Agente responsável</span>
            <select data-testid="filter-agent" value={filters.agent_id} onChange={set("agent_id")}
                    className={`${field} w-full`}>
              <option value="">Todos os agentes</option>
              <option value="none">Sem agente atribuído</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">Depósitos</span>
            <select data-testid="filter-deposits" value={filters.deposits} onChange={set("deposits")}
                    className={`${field} w-full`}>
              <option value="">Todos</option>
              <option value="none">Sem depósito</option>
              <option value="any">Com depósito aprovado</option>
              <option value="pending">Depósito pendente</option>
            </select>
          </label>

          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">Criado de</span>
            <input type="date" data-testid="filter-created-from" value={filters.created_from}
                   onChange={set("created_from")} className={`${field} w-full`} />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">Criado até</span>
            <input type="date" data-testid="filter-created-to" value={filters.created_to}
                   onChange={set("created_to")} className={`${field} w-full`} />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">Último login de</span>
            <input type="date" data-testid="filter-login-from" value={filters.last_login_from}
                   onChange={set("last_login_from")} className={`${field} w-full`} />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">Último login até</span>
            <input type="date" data-testid="filter-login-to" value={filters.last_login_to}
                   onChange={set("last_login_to")} className={`${field} w-full`} />
          </label>

          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">Depositado ≥ (USD)</span>
            <input type="number" min="0" data-testid="filter-min-deposited" value={filters.min_deposited}
                   onChange={set("min_deposited")} className={`${field} w-full`} />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">Saldo mínimo</span>
            <input type="number" data-testid="filter-min-balance" value={filters.min_balance}
                   onChange={set("min_balance")} className={`${field} w-full`} />
          </label>
          <label className="space-y-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#94A3B8]">Saldo máximo</span>
            <input type="number" data-testid="filter-max-balance" value={filters.max_balance}
                   onChange={set("max_balance")} className={`${field} w-full`} />
          </label>

          <button data-testid="reset-filters" onClick={onReset}
                  className="mt-auto flex h-9 items-center justify-center gap-2 rounded-md border border-[#22252F] text-xs text-[#94A3B8] transition-colors hover:border-[#EF4444]/50 hover:text-[#EF4444]">
            <X className="h-3.5 w-3.5" /> Limpar filtros
          </button>
        </div>
      ) : null}
    </div>
  );
};
